import type { OrderLineItem, Resolution, ResolutionOption, Shop } from "../types";

type VariantOptionInfo = { name: string; value: string };
type VariantInfo = {
  id?: string | number;
  inventory_quantity: number;
  inventoryPolicy?: string;
  selectedOptions?: VariantOptionInfo[];
};
type ProductInfo = { variants: VariantInfo[] };
export type VariantCollection = { products: ProductInfo[] };

export type OrderOpt = {
  orderCanBeRefunded?: boolean;
  orderCanBeExchanged?: boolean;
  orderCanBeExchangedWithCredit?: boolean;
  orderCanBeDiscounted?: boolean;
  optByLineItemId?: Record<
    string,
    {
      productCanBeRefund?: boolean;
      productCanBeExchanged?: boolean;
      productCanBeExchangedWithCredit?: boolean;
      productCanBeDiscounted?: boolean;
    }
  >;
};

type ShopExchange = {
  activated?: boolean;
  available?: boolean;
  minimalStock?: number;
  exchangeWithOtherArticles?: boolean;
};
type ShopDiscount = { activated?: boolean; available?: boolean };
type ShopPreferences = { nonReturnableItems?: string[] };
type ShopForResolutions = Shop & {
  exchange?: ShopExchange;
  discount?: ShopDiscount;
  preferences?: ShopPreferences;
};
type LineItemForResolutions = OrderLineItem & {
  product_id?: string;
  line_item_id?: string;
  variant?: { variant_id?: string | number };
};

function isRealVariant(v: VariantInfo): boolean {
  return (v.selectedOptions ?? []).some((o) => o.value !== "Default Title");
}

function isInStock(v: VariantInfo, minimalStock: number): boolean {
  return v.inventoryPolicy === "CONTINUE" || v.inventory_quantity > minimalStock;
}

/**
 * A variant swap needs a variant the customer does not already own. A product
 * sold in a single size ("TU") has an in-stock real variant and still nothing
 * to swap for: offering the option there leads to a step with no selector.
 */
function hasAlternativeVariants(
  collection: VariantCollection,
  minimalStock: number,
  currentVariantId: string,
): boolean {
  return collection.products.some((p) =>
    p.variants.some(
      (v) =>
        isRealVariant(v) &&
        isInStock(v, minimalStock) &&
        String(v.id ?? "") !== currentVariantId,
    ),
  );
}

export function getAvailableResolutions(
  shop: Shop,
  lineItem: OrderLineItem,
  variantCollection?: VariantCollection | null,
  opt?: OrderOpt | null,
): Resolution[] {
  const shopTyped = shop as ShopForResolutions;
  const lineItemTyped = lineItem as LineItemForResolutions;
  const lineItemId = lineItemTyped.line_item_id ?? "";
  const lineOpt = opt?.optByLineItemId?.[lineItemId];

  const nonReturnable: string[] = shopTyped.preferences?.nonReturnableItems ?? [];
  if (lineItemTyped.product_id && nonReturnable.includes(lineItemTyped.product_id)) {
    return [];
  }

  const available: Resolution[] = [];

  const refundAllowed =
    opt?.orderCanBeRefunded !== false && lineOpt?.productCanBeRefund !== false;
  if (refundAllowed) available.push("refund");

  const exchangeEnabled =
    shopTyped.exchange?.activated === true && shopTyped.exchange?.available === true;

  const exchangeAllowed =
    opt?.orderCanBeExchanged !== false && lineOpt?.productCanBeExchanged !== false;

  if (exchangeEnabled && exchangeAllowed) {
    const minimalStock: number = shopTyped.exchange?.minimalStock ?? 0;
    const variantsAvailable =
      !variantCollection ||
      (variantCollection.products.length > 0 &&
        hasAlternativeVariants(
          variantCollection,
          minimalStock,
          String(lineItemTyped.variant?.variant_id ?? ""),
        ));

    if (variantsAvailable) {
      available.push("exchange");
    }
  }

  const discountEnabled =
    shopTyped.discount?.activated === true && shopTyped.discount?.available === true;
  const discountAllowed =
    opt?.orderCanBeDiscounted !== false && lineOpt?.productCanBeDiscounted !== false;
  if (discountEnabled && discountAllowed) {
    available.push("discount");
  }

  const exchangeCreditAllowed =
    opt?.orderCanBeExchangedWithCredit !== false &&
    lineOpt?.productCanBeExchangedWithCredit !== false;
  if (
    exchangeEnabled &&
    shopTyped.exchange?.exchangeWithOtherArticles &&
    exchangeCreditAllowed
  ) {
    available.push("exchange-credit");
  }

  return available;
}

export type SingleFamilyLeaf = {
  resolution: Resolution;
  resolutionOption: ResolutionOption | null;
} | null;

export type LeafOptions = {
  /**
   * Catalog mode (merchant previewing the widget outside the storefront):
   * exchange-credit then requires staging articles in the catalog picker, so
   * it needs its own step and cannot be committed straight away.
   */
  catalogSearchActive?: boolean;
};

/**
 * Returns the sole leaf for a family if it has exactly one available
 * sub-option that requires no further input (i.e. everything except
 * variant-swap exchange, which always needs a manual variant pick).
 * Returns null if the family has 0 or 2+ options, or its only option
 * needs a variant pick.
 */
export function getAutoCommittableLeaf(
  family: "refund" | "exchange",
  rawResolutions: Resolution[],
  options: LeafOptions = {},
): SingleFamilyLeaf {
  if (family === "refund") {
    const hasBankTransfer = rawResolutions.includes("refund");
    const hasDiscount = rawResolutions.includes("discount");
    if (hasBankTransfer && !hasDiscount) {
      return { resolution: "refund", resolutionOption: "bank-transfer" };
    }
    if (hasDiscount && !hasBankTransfer) {
      return { resolution: "discount", resolutionOption: "discount-code" };
    }
    return null;
  }

  const hasVariant = rawResolutions.includes("exchange");
  const hasCredit = rawResolutions.includes("exchange-credit");
  if (hasCredit && !hasVariant && !options.catalogSearchActive) {
    return { resolution: "exchange-credit", resolutionOption: null };
  }
  return null;
}
